import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {CustomTag} from 'folder-items/FileConstants';
import TagTable from 'metadata/TagTable';
import notifications from 'notifications';
import * as TagUtil from 'schemas/tags/TagUtil';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';

export interface DataCatalogTagTableProps {
  itemId: string;
  emptyMessage: string;
  initialTags: Array<CustomTag>;
  isWritable: boolean;
  updateTags: (newTags: Array<CustomTag>) => Promise<void>;
}

export default function DataCatalogTagTable(props: DataCatalogTagTableProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState(props.initialTags);

  const handleCancelUpdate = (): void => {
    setTags(props.initialTags);
    setIsEditing(false);
  };

  const validateTags = (): string | null => {
    const tagNames = tags.map((tag) => tag.name);
    if (tagNames.some((tagName) => !tagName)) {
      return 'Field names cannot be empty.';
    }
    if (tagNames.length !== new Set(tagNames).size) {
      return 'Two fields cannot have the same name.';
    }

    return null;
  };

  const handleCompleteUpdate = (): void => {
    // If updates are invalid, show error and stay in edit mode.
    const error = validateTags();
    if (error) {
      notifications.error(error);
      return;
    }
    if (!TagUtil.areTagsValidAndNotify(tags)) {
      return;
    }

    // If updates are valid, save updates.
    setIsEditing(false);
    handleSaveUpdates(tags);
  };

  const handleSaveUpdates = (newTags: Array<CustomTag>): void => {
    if (!isEqual(newTags, props.initialTags)) {
      props
        .updateTags(newTags)
        .then(() => {
          setIsEditing(false);
        })
        .catch((error) => {
          notifications.notifyError(error);
          setIsEditing(true);
        });
    }
  };

  return (
    <TagTable
      canAddOrRemoveTags={props.isWritable}
      emptyMessage={props.emptyMessage}
      fileId={props.itemId}
      tags={tags}
      areTagsEditable={props.isWritable}
      isNameEditable={props.isWritable}
      title="Custom Field"
      isEditing={isEditing}
      onUpdateTag={(tagIndex: number, newValue: CustomTag) => {
        const newTags = [...tags];
        newTags[tagIndex] = {...tags[tagIndex], ...newValue};
        setTags(newTags);
      }}
      onCreateTag={() => {
        setTags([...tags, {name: '', value: ''}]);
        setIsEditing(true);
      }}
      onRemoveTag={(tagIndex) => {
        const newTags = [...tags];
        newTags.splice(tagIndex, 1);
        setTags(newTags);
        // Due to the way TagTable works, it leaves editing mode when the last tag is removed,
        // so save in that case
        if (!isEditing || newTags.length === 0) {
          handleSaveUpdates(newTags);
        }
      }}
      onEdit={() => {
        setIsEditing(true);
      }}
      onCompleteUpdate={handleCompleteUpdate}
      onCancelUpdate={handleCancelUpdate}
      onKeyDown={(e) => {
        if (isEnterKey(e)) {
          e.preventDefault();
          handleCompleteUpdate();
        } else if (isEscapeKey(e)) {
          e.preventDefault();
          handleCancelUpdate();
        }
      }}
    />
  );
}
